import { FormlyFieldConfig } from '@ngx-formly/core';
import { AgreementsFormOptions } from './agreements-form-options.interface';
import { toFormlyOptions } from '@lmc-app/applications/applications-form-helpers';

export function getPrimaryAgreementFields(
    options: Partial<AgreementsFormOptions>,
    maxCalendarDate: string | Date,
    updateAgreement: boolean,
    customFieldClass: string): FormlyFieldConfig[] {
    // Two groups: Details (personal) and Banking.
    // DOB + Mobile on one row. Address block layout: Unit Number + Address on one row, City + Postal Code on the next, Province on its own row
    const colClass = (col: string) => customFieldClass.replace(/\bcol-12\b/, col);
    return [
        {
            className: 'col-12',
            fieldGroupClassName: 'row',
            ...(updateAgreement)
            ?
            {
                wrappers: ['expansion-panel'],
                props: {
                    label: 'Details',
                    groupHeading: 'Primary Applicant',
                    icon: 'person',
                    description: 'Name, contact & address',
                    expanded: false,
                    expClass: 'exp-class exp-group exp-group--first'
                },
            } : {},
            fieldGroup: [
                {
                    key: 'FirstName.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Firstname',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {                        
                            'props.required': '',
                        }
                        :  
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.FirstName?.isValid === true,
                        },
                    }
                },
                {
                    key: 'MiddleName.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'MiddleName',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {                        
                            'props.required': '',
                        }
                        :  
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.MiddleName?.isValid === true,
                        },
                    }
                },            
                {
                    key: 'LastName.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'LastName',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {                        
                            'props.required': '',
                        }
                        :  
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.LastName?.isValid === true,
                        },
                    }
                },
                // {
                //     key: 'DealName.value',
                //     type: 'input', // Automatically creates a text input
                //     className: customFieldClass,
                //     props: {
                //         label: 'Deal Name',
                //         appearance: 'outline', 
                //         floatLabel: 'always',
                //     },
                //     expressions: {
                //         ...(updateAgreement) ?
                //         {                        
                //             'props.required': 'model.DealName?.required',
                //         }
                //         :  
                //         {
                //             hide: (field: FormlyFieldConfig) =>
                //             field.model?.DealName?.isValid === true,
                //         },
                //     }
                // },
            
                {
                    key: 'DOB.value',
                    type: 'datepicker', 
                    className: colClass('col-12 col-md-6 pe-md-1'),
                    props: {
                        label: 'DOB',
                        appearance: 'outline', 
                        floatLabel: 'always',
                        datepickerOptions: {
                        max: maxCalendarDate
                        }
                    },
                    expressions: {                                       
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        : 
                        {
                            hide: (field: FormlyFieldConfig) =>
                                field.model?.DOB?.isValid === true,
                                'props.max': (field: FormlyFieldConfig) => {
                                return maxCalendarDate;
                            }

                        }                    
                    },
                },
                {
                    key: 'Mobile.value',
                    type: 'input', // Automatically creates a text input
                    className: colClass('col-12 col-md-6 ps-md-1'),
                    props: {
                        label: 'Mobile',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {                                                          
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        : 
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.Mobile?.isValid === true,
                        }                    
                    },
                },
                {
                    key: 'Email.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Email',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {                                        
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        : 
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.Email?.isValid === true,
                        }                    
                    },
                },
                {
                    key: 'UnitNumber.value',
                    type: 'input',
                    className: colClass('col-12 col-md-4 pe-md-1'),
                    props: {
                        label: 'Unit Number',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {                                      
                        ...(updateAgreement) ?
                        {}
                        :
                        { hide: (field: FormlyFieldConfig) =>
                            field.model?.UnitNumber?.field_group === "OPTIONAL"
                        }
                    }
                },
                {
                    key: 'Address.value',
                    type: 'input',
                    className: colClass('col-12 col-md-8 ps-md-1'),
                    props: {
                        label: 'Address',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        { 
                            'props.required': '',
                        }
                        : 
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.Address?.isValid === true,
                        }
                    },
                },
                {
                    key: 'City.value',
                    type: 'input',
                    className: colClass('col-12 col-md-6 pe-md-1'),
                    props: {
                        label: 'City',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {                    
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        : 
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.City?.isValid === true,
                        }                    
                    },
                },
                {
                    key: 'PostalCode.value',
                    type: 'input', 
                    className: colClass('col-12 col-md-6 ps-md-1'),
                    props: {
                        label: 'Postal Code',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        : 
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.PostalCode?.isValid === true,
                        }                    
                    },
                },
                {
                    key: 'FullProvince.value',
                    type: 'searchable-select',
                    className: customFieldClass,
                    props: {
                        label: 'Province',         
                        appearance: 'outline', 
                        floatLabel: 'always',
                        options: toFormlyOptions(options.provinces$),
                        searchPlaceholder: 'Search provinces',
                        noEntriesFoundLabel: 'No matching province found',
                    },
                    expressions: {                    
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        : 
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.Province?.isValid === true,
                        }                    
                    },
                },
                {
                    key: 'NoCoAppLPP.value',
                    className: customFieldClass,
                    type: 'yes-no-toggle',
                    props: {
                        label: 'No LPP Co-Applicant Included?',
                    },
                    expressions: {
                        hide: (field: FormlyFieldConfig) => field.model?.HasCoApp?.value !== 'Yes',
                    },
                },
                // // {
                // //     key: 'MainCreditorPortfolio.value',
                // //     type: 'select', // Automatically creates a text input
                // //     className: customFieldClass,
                // //     props: {
                // //         label: 'Creditor Portfolio',
                // //         appearance: 'outline', 
                // //         floatLabel: 'always',
                // //         options: [
                // //             { label: 'Purchase', value: 'Purchase' },
                // //             { label: 'Contingency', value: 'Contingency' },
                // //         ]
                // //     },
                // // },             
                // // {
                // //     key: 'MainCustomSettlement.value',
                // //     type: 'select', // Automatically creates a text input
                // //     className: customFieldClass,
                // //     props: {
                // //         label: 'Custom Settlement',
                // //         appearance: 'outline', 
                // //         floatLabel: 'always',
                // //         options: [
                // //             { label: 'Yes', value: 'Yes' },
                // //             { label: 'No', value: 'No' },
                // //         ],
                // //     },
                // // },
            ]
        },
        {
            className: 'col-12',
            fieldGroupClassName: 'row',
            ...(updateAgreement)
            ?
            {
                wrappers: ['expansion-panel'],
                props: {
                    label: 'Banking',
                    icon: 'account_balance',
                    description: 'Institution & account',
                    expanded: false,
                    expClass: 'exp-class exp-group exp-group--last'
                },
            } : {},
            fieldGroup: [
                {
                    key: 'FinancialInstitutionName.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Financial Institution Name',
                        appearance: 'outline',
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        :
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.FinancialInstitutionName?.isValid === true,
                        }
                    },
                },
                {
                    key: 'FinancialInstitutionNumber.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Financial Institution Number',
                        appearance: 'outline',
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        :
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.FinancialInstitutionNumber?.isValid === true,
                        }
                    },
                },
                {
                    key: 'BankAccountNumber.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Bank Account Number',
                        appearance: 'outline',
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        :
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.BankAccountNumber?.isValid === true,
                        }
                    },
                },
                {
                    key: 'BankTransitNo.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Bank Transit No.',
                        appearance: 'outline',
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        :
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.BankTransitNo?.isValid === true,
                        }
                    },
                },
                {
                    key: 'BranchAddress.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Branch Address',
                        appearance: 'outline',
                        floatLabel: 'always',
                    },
                    expressions: {
                        ...(updateAgreement) ?
                        {
                            'props.required': '',
                        }
                        :
                        {
                            hide: (field: FormlyFieldConfig) =>
                            field.model?.BranchAddress?.isValid === true,
                        }
                    },
                },
            ]
        },
    ];
}
